import type {
  AuthProviderId,
  CardDefinition,
  ClientGameView,
  CsvColumnMapping,
  DeckExportFormat,
  DeckImportResult,
  DeckStrengthSummary,
  FormatId,
  FormatLeaderboardEntry,
  GameIntent,
  ImportedDeckPayload,
  ImporterSourceId,
  PlainTextParseResult,
  SessionType,
  TableSettings,
  TrackingProviderId,
} from '#shared';
import type {
  CartDeckItem,
  CartOptimizationPreferences,
  OptimizedDeckCartResult,
  ReoptimizedCartOutcome,
  StockVerificationUpdate,
  VendorQuote,
} from '#cart-optimizer';

export interface DeckPriceAlertDTO {
  alertId: string;
  deckName: string;
  targetTotalUsd: number;
  lastCheckedTotalUsd: number;
  triggered: boolean;
  triggeredAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface SavedDeckDTO {
  deckId: string;
  accountId: string;
  shareSlug: string;
  name: string;
  format: FormatId;
  deck: ImportedDeckPayload;
  cosmetics: {
    sleeveCardName: string | null;
    playmatCardName: string | null;
    focalPoint?: 'top' | 'center' | 'bottom';
  };
  exactHash: string;
  eraNumber: number;
  trackedGames: number;
  isPublic: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface AccountProfileDTO {
  accountId: string;
  publicId: string;
  displayName: string;
  avatarUrl: string | null;
  profileColor: string;
  verifiedProvider: AuthProviderId | null;
  completedGames: number;
  leaderboardOptIn?: boolean;
  isStaff: boolean;
  blockedPublicIds?: string[];
  linkedProviders: {
    provider: AuthProviderId;
    providerHandle: string;
    linkedAt: number;
  }[];
  notifications: {
    id: string;
    kind: string;
    message: string;
    createdAt: number;
  }[];
}

export interface ScheduledGameSignupDTO {
  publicId: string;
  displayName: string;
  profileColor: string;
  verifiedProvider: AuthProviderId | null;
  status: 'confirmed' | 'waitlist' | 'pending_approval' | 'declined';
  streamConsentAccepted: boolean;
  deckName: string | null;
  commanderName: string | null;
  createdAt: number;
}

export interface ScheduledGameItemDTO {
  eventId: string;
  hostPublicId: string;
  hostDisplayName: string;
  hostProfileColor: string;
  title: string;
  description: string;
  format: FormatId;
  bracket: number | null;
  maxSeats: number;
  startsAt: number;
  durationMin: number;
  recurrence: 'none' | 'weekly' | 'biweekly';
  approvalMode: 'first_come' | 'host_approved';
  isStreamed: boolean;
  streamUrl: string | null;
  launchedTableId: string | null;
  confirmedCount: number;
  waitlistCount: number;
  openSeats: number;
  signups: ScheduledGameSignupDTO[];
  createdAt: number;
}

import { handleLocalSandboxApiRequest } from './localSandboxEngine.ts';

let csrfTokenCache: string | null = null;
let localSandboxMode = false;

export function isLocalSandboxMode(): boolean {
  return localSandboxMode;
}

export function setCsrfToken(token: string | null): void {
  csrfTokenCache = token;
}

export function getPlaytableHttpOrigin(): string {
  const envOrigin = (import.meta as any).env?.VITE_PLAYTABLE_ORIGIN;
  return typeof envOrigin === 'string' && envOrigin.trim()
    ? envOrigin.trim().replace(/\/+$/, '')
    : '';
}

export function getPlaytableWsBaseUrl(): string {
  const httpOrigin = getPlaytableHttpOrigin();
  if (httpOrigin) {
    return `${httpOrigin.replace(/^http/i, 'ws')}/ws`;
  }
  const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProto}//${window.location.host}/ws`;
}

export function getSessionBaseUrl(): string {
  const path =
    window.location.pathname && window.location.pathname !== '/'
      ? window.location.pathname.replace(/\/+$/, '')
      : '/';
  return `${window.location.origin}${path}`;
}

export async function apiRequest<T>(
  path: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  const method = options.method ?? 'GET';

  if (localSandboxMode && path.startsWith('/api/')) {
    return handleLocalSandboxApiRequest<T>(path, method, options.body);
  }

  const headers: Record<string, string> = {};
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (csrfTokenCache && method !== 'GET') {
    headers['X-CSRF-Token'] = csrfTokenCache;
  }

  const fullUrl = path.startsWith('http') ? path : `${getPlaytableHttpOrigin()}${path}`;
  let res: Response;
  try {
    res = await fetch(fullUrl, {
      method,
      credentials: 'include',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  } catch (err) {
    if (path.startsWith('/api/')) {
      localSandboxMode = true;
      return handleLocalSandboxApiRequest<T>(path, method, options.body);
    }
    throw err;
  }

  const contentType = (res.headers.get('content-type') ?? '').toLowerCase();
  if (path.startsWith('/api/') && !contentType.includes('application/json')) {
    localSandboxMode = true;
    return handleLocalSandboxApiRequest<T>(path, method, options.body);
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error ?? `Request failed (${res.status})`);
  }
  return data as T;
}

export const playtableApi = {
  checkHealth() {
    return apiRequest<{ ok: boolean; devAuthEnabled: boolean; domain: string }>('/api/health');
  },

  getMe() {
    return apiRequest<{
      authenticated: boolean;
      profile: AccountProfileDTO | null;
      csrfToken: string | null;
    }>('/api/auth/me');
  },

  devLogin(params: {
    provider: AuthProviderId;
    handle: string;
    mode?: 'signin' | 'link' | 'reauth';
  }) {
    return apiRequest<{
      authenticated: boolean;
      profile: AccountProfileDTO;
      csrfToken: string;
    }>('/api/auth/dev-login', { method: 'POST', body: params });
  },

  startOAuth(provider: AuthProviderId, mode: 'signin' | 'link' | 'reauth' = 'signin') {
    return apiRequest<{ state: string; authorizeUrl: string }>('/api/auth/oauth/start', {
      method: 'POST',
      body: { provider, mode },
    });
  },

  unlinkProvider(provider: AuthProviderId) {
    return apiRequest<{ profile: AccountProfileDTO }>('/api/auth/unlink', {
      method: 'POST',
      body: { provider },
    });
  },

  updateProfile(patch: {
    displayName?: string;
    profileColor?: string;
    verifiedProvider?: AuthProviderId | null;
    leaderboardOptIn?: boolean;
  }) {
    return apiRequest<{ profile: AccountProfileDTO }>('/api/account/profile', {
      method: 'PATCH',
      body: patch,
    });
  },

  blockPlayer(targetPublicId: string) {
    return apiRequest<{ ok: boolean; blockedPublicIds: string[] }>('/api/account/blocks', {
      method: 'POST',
      body: { targetPublicId },
    });
  },

  unblockPlayer(targetPublicId: string) {
    return apiRequest<{ ok: boolean; blockedPublicIds: string[] }>('/api/account/blocks', {
      method: 'DELETE',
      body: { targetPublicId },
    });
  },

  reportPlayer(params: { targetPublicId: string; tableId?: string; reason: string; details: string }) {
    return apiRequest<{ ok: boolean; reportId: string }>('/api/moderation/report', {
      method: 'POST',
      body: params,
    });
  },

  listTrackingWebhooks() {
    return apiRequest<{
      webhooks: {
        provider: TrackingProviderId;
        webhookUrl: string;
        hasToken: boolean;
        enabled: boolean;
        syncedCount: number;
        updatedAt: number;
      }[];
    }>('/api/account/tracking-webhooks');
  },

  saveTrackingWebhook(params: {
    provider: TrackingProviderId;
    webhookUrl: string;
    apiToken?: string;
    enabled: boolean;
  }) {
    return apiRequest<{
      webhooks: {
        provider: TrackingProviderId;
        webhookUrl: string;
        hasToken: boolean;
        enabled: boolean;
        syncedCount: number;
        updatedAt: number;
      }[];
    }>('/api/account/tracking-webhooks', {
      method: 'POST',
      body: params,
    });
  },

  exportAccount() {
    return apiRequest<Record<string, unknown>>('/api/account/export');
  },

  deleteAccount() {
    return apiRequest<{ ok: boolean }>('/api/account', { method: 'DELETE' });
  },

  signOut() {
    return apiRequest<{ ok: boolean }>('/api/auth/signout', { method: 'POST' });
  },

  signOutEverywhere() {
    return apiRequest<{ ok: boolean }>('/api/auth/signout-all', { method: 'POST' });
  },

  listPublicTables(filters?: {
    format?: FormatId | '';
    bracket?: number | '';
    seatsOpen?: boolean;
    trackedOnly?: boolean;
    hidePassword?: boolean;
    language?: string;
    search?: string;
  }) {
    const qs = new URLSearchParams();
    if (filters?.format) qs.set('format', filters.format);
    if (filters?.bracket) qs.set('bracket', String(filters.bracket));
    if (filters?.seatsOpen) qs.set('seatsOpen', '1');
    if (filters?.trackedOnly) qs.set('trackedOnly', '1');
    if (filters?.hidePassword) qs.set('hidePassword', '1');
    if (filters?.language) qs.set('language', filters.language);
    if (filters?.search) qs.set('search', filters.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<{
      tables: {
        tableId: string;
        inviteCode: string;
        name: string;
        format: FormatId;
        status: string;
        seatsFilled: number;
        maxSeats: number;
        spectatorsAllowed: boolean;
        allowLateJoinRequests?: boolean;
        hasPassword?: boolean;
        isTracked?: boolean;
        competitiveMode?: boolean;
        bracket?: 1 | 2 | 3 | 4 | 5 | null;
        language?: string;
        description?: string;
        region: string;
      }[];
    }>(`/api/tables/public${suffix}`);
  },

  listMyGames() {
    return apiRequest<{
      resumable: {
        tableId: string;
        inviteCode: string;
        name: string;
        format: FormatId;
        status: string;
        turnNumber: number;
        updatedAt: number;
      }[];
      history: {
        tableId: string;
        format: string;
        status: string;
        createdAt: number;
        displayNameSnapshot: string;
      }[];
    }>('/api/tables/mine');
  },

  createTable(params: {
    format: FormatId;
    settings?: Partial<TableSettings>;
    password?: string;
    quickStartGoldfish?: boolean;
    initialDeck?: ImportedDeckPayload | null;
  }) {
    return apiRequest<{
      tableId: string;
      inviteCode: string;
      overlaySecret: string;
      wsTicket: string;
    }>('/api/tables', { method: 'POST', body: params });
  },

  joinTable(params: {
    tableId?: string;
    inviteCode?: string;
    password?: string;
    asSpectator?: boolean;
    sessionType?: SessionType;
  }) {
    return apiRequest<{
      tableId: string;
      inviteCode: string;
      seatId: string | null;
      sessionType: SessionType;
      wsTicket: string;
    }>('/api/tables/join', { method: 'POST', body: params });
  },

  requestLateJoin(tableId: string, password?: string) {
    return apiRequest<{ ok: boolean; requestId: string }>(
      `/api/tables/${encodeURIComponent(tableId)}/late-join`,
      { method: 'POST', body: { password } }
    );
  },

  resolveLateJoin(tableId: string, requestId: string, approve: boolean) {
    return apiRequest<{ ok: boolean; seatId?: string }>(
      `/api/tables/${encodeURIComponent(tableId)}/late-join/resolve`,
      { method: 'POST', body: { requestId, approve } }
    );
  },

  getQueueStatus() {
    return apiRequest<{
      status: 'idle' | 'queued' | 'matched';
      ticketId?: string;
      format?: FormatId;
      mode?: 'unranked' | 'ranked';
      matchingStyle?: 'random_fill' | 'balanced';
      waitedSec?: number;
      estimatedWaitSec?: number;
      matchedTableId?: string;
      matchedInviteCode?: string;
      queuePopulation: number;
    }>('/api/queues/status');
  },

  enqueueMatchmaking(params: {
    format: FormatId;
    mode: 'unranked' | 'ranked';
    matchingStyle: 'random_fill' | 'balanced';
    preferredPodSizes?: number[];
    bracket?: 1 | 2 | 3 | 4 | 5;
    deck?: ImportedDeckPayload | null;
    allowPublicBackfill?: boolean;
  }) {
    return apiRequest<{
      status: 'queued' | 'matched';
      ticketId: string;
      estimatedWaitSec: number;
      matchedTableId?: string;
      matchedInviteCode?: string;
    }>('/api/queues/enqueue', { method: 'POST', body: params });
  },

  leaveQueue() {
    return apiRequest<{ ok: boolean }>('/api/queues/leave', { method: 'POST' });
  },

  declineMatchedPod(tableId: string) {
    return apiRequest<{ ok: boolean; penaltyApplied: false; requeuedCount: number }>(
      `/api/queues/pods/${encodeURIComponent(tableId)}/decline`,
      { method: 'POST' }
    );
  },

  getMatchReportPrefill(tableId: string) {
    return apiRequest<{
      tableId: string;
      format: FormatId;
      isTracked: boolean;
      turnNumber: number;
      winnerSeatIds: string[];
      isDraw: boolean;
      seats: {
        seatId: string;
        displayName: string;
        accountId: string | null;
        deckId: string | null;
        eraNumber: number;
        bracket: 1 | 2 | 3 | 4 | 5;
        eliminatedTurn: number | null;
        suggestedPlacement: number;
        isWinner: boolean;
      }[];
      existingReport: {
        winnerSeatIds: string[];
        isDraw: boolean;
        lockedAt: number;
        reportedAt: number;
      } | null;
    }>(`/api/tables/${encodeURIComponent(tableId)}/report`);
  },

  submitMatchReport(
    tableId: string,
    params: {
      winnerSeatIds: string[];
      isDraw?: boolean;
      eliminationTurnsBySeat?: Record<string, number | null>;
      winTurn?: number;
    }
  ) {
    return apiRequest<{
      result: { winnerSeatIds: string[]; isDraw: boolean; lockedAt: number };
      deckStrengths: Record<string, DeckStrengthSummary>;
      syncedProviders: TrackingProviderId[];
    }>(`/api/tables/${encodeURIComponent(tableId)}/report`, {
      method: 'POST',
      body: params,
    });
  },

  getDeckStrength(deckId: string, bracket = 3) {
    return apiRequest<{ strength: DeckStrengthSummary }>(
      `/api/decks/${encodeURIComponent(deckId)}/strength?bracket=${bracket}`
    );
  },

  getFormatLeaderboard(format: FormatId) {
    return apiRequest<{
      format: FormatId;
      entries: FormatLeaderboardEntry[];
      myStanding: {
        format: FormatId;
        qualified: boolean;
        games90d: number;
        minGamesRequired: number;
        distinctDecks90d: number;
        minDecksRequired: number;
        rating: number | null;
        winRatePct: number | null;
      } | null;
    }>(`/api/leaderboards/${encodeURIComponent(format)}`);
  },

  getReplayFrame(tableId: string, step?: number) {
    const qs = step !== undefined ? `?step=${step}` : '';
    return apiRequest<{
      tableId: string;
      totalSteps: number;
      currentStep: number;
      milestones: { step: number; seq: number; turnNumber: number; type: string; summary: string }[];
      frame: ClientGameView;
    }>(`/api/tables/${encodeURIComponent(tableId)}/replay${qs}`);
  },

  mintWsTicket(tableId: string, sessionType: SessionType = 'table') {
    return apiRequest<{ wsTicket: string; tableId: string; sessionType: SessionType }>(
      `/api/tables/${encodeURIComponent(tableId)}/ws-ticket`,
      { method: 'POST', body: { sessionType } }
    );
  },

  parseDeckText(text: string, name: string, format: FormatId) {
    return apiRequest<PlainTextParseResult>('/api/decks/parse', {
      method: 'POST',
      body: { text, name, format },
    });
  },

  searchCards(q: string, live = false) {
    return apiRequest<{ cards: CardDefinition[] }>(
      `/api/cards/search?q=${encodeURIComponent(q)}&live=${live ? '1' : '0'}`
    );
  },

  importDeckUniversal(params: {
    rawInput: string;
    filename?: string;
    deckName?: string;
    format?: FormatId;
    csvMapping?: CsvColumnMapping;
    forceSource?: ImporterSourceId;
  }) {
    return apiRequest<DeckImportResult>('/api/decks/import', {
      method: 'POST',
      body: params,
    });
  },

  importDeckZip(zipBase64: string, format: FormatId, saveAllToLibrary = true) {
    return apiRequest<{ decks: DeckImportResult[]; totalFiles: number }>('/api/decks/import-zip', {
      method: 'POST',
      body: { zipBase64, format, saveAllToLibrary },
    });
  },

  exportDeck(deck: ImportedDeckPayload, exportFormat: DeckExportFormat) {
    return apiRequest<{ text: string; exportFormat: DeckExportFormat }>('/api/decks/export', {
      method: 'POST',
      body: { deck, exportFormat },
    });
  },

  listSavedDecks() {
    return apiRequest<{ decks: SavedDeckDTO[] }>('/api/decks');
  },

  saveDeck(params: {
    deckId?: string;
    deck: ImportedDeckPayload;
    cosmetics?: SavedDeckDTO['cosmetics'];
    isPublic?: boolean;
  }) {
    return apiRequest<{ savedDeck: SavedDeckDTO }>('/api/decks', {
      method: 'POST',
      body: params,
    });
  },

  deleteSavedDeck(deckId: string) {
    return apiRequest<{ ok: boolean }>(`/api/decks/${encodeURIComponent(deckId)}`, {
      method: 'DELETE',
    });
  },

  getSharedDeck(shareSlug: string) {
    return apiRequest<{
      sharedDeck: {
        shareSlug: string;
        name: string;
        format: FormatId;
        deck: ImportedDeckPayload;
        eraNumber: number;
        updatedAt: number;
      };
    }>(`/api/decks/share/${encodeURIComponent(shareSlug)}`);
  },

  copySharedDeckToLibrary(shareSlug: string) {
    return apiRequest<{ savedDeck: SavedDeckDTO }>(
      `/api/decks/share/${encodeURIComponent(shareSlug)}/copy`,
      { method: 'POST' }
    );
  },

  // ---- PHASE 7: STREAMED GAMES CALENDAR, COMPANION QR PAIRING & OBS OVERLAY ----
  listScheduledGames(filters?: { format?: FormatId | ''; streamedOnly?: boolean; openSlotsOnly?: boolean }) {
    const qs = new URLSearchParams();
    if (filters?.format) qs.set('format', filters.format);
    if (filters?.streamedOnly) qs.set('streamedOnly', '1');
    if (filters?.openSlotsOnly) qs.set('openSlotsOnly', '1');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<{ games: ScheduledGameItemDTO[] }>(`/api/calendar${suffix}`);
  },

  createScheduledGame(params: {
    title: string;
    description?: string;
    format: FormatId;
    bracket?: number | null;
    maxSeats?: number;
    startsAt: number;
    durationMin?: number;
    recurrence?: 'none' | 'weekly' | 'biweekly';
    approvalMode?: 'first_come' | 'host_approved';
    isStreamed?: boolean;
    streamUrl?: string | null;
    hostTakesSeat?: boolean;
    hostCommanderName?: string | null;
  }) {
    return apiRequest<{ game: ScheduledGameItemDTO }>('/api/calendar', {
      method: 'POST',
      body: params,
    });
  },

  signUpScheduledGame(
    eventId: string,
    params: { streamConsentAccepted: boolean; deckName?: string; commanderName?: string }
  ) {
    return apiRequest<{ game: ScheduledGameItemDTO }>(
      `/api/calendar/${encodeURIComponent(eventId)}/signup`,
      { method: 'POST', body: params }
    );
  },

  withdrawScheduledGame(eventId: string) {
    return apiRequest<{ game: ScheduledGameItemDTO; promotedPublicId: string | null }>(
      `/api/calendar/${encodeURIComponent(eventId)}/withdraw`,
      { method: 'POST' }
    );
  },

  approveScheduledSignup(eventId: string, targetPublicId: string, approve: boolean) {
    return apiRequest<{ game: ScheduledGameItemDTO }>(
      `/api/calendar/${encodeURIComponent(eventId)}/approve`,
      { method: 'POST', body: { targetPublicId, approve } }
    );
  },

  launchScheduledGame(eventId: string) {
    return apiRequest<{
      tableId: string;
      inviteCode: string;
      overlaySecret: string;
      nextRecurringEventId: string | null;
    }>(`/api/calendar/${encodeURIComponent(eventId)}/launch`, { method: 'POST' });
  },

  createCompanionPairCode(
    tableId: string,
    purpose: 'private_hand' | 'obs_dock' | 'overhead_camera' = 'private_hand'
  ) {
    return apiRequest<{
      code: string;
      pairUrl: string;
      qrSvg: string;
      purpose: 'private_hand' | 'obs_dock' | 'overhead_camera';
      expiresAt: number;
    }>(`/api/tables/${encodeURIComponent(tableId)}/pair-code`, {
      method: 'POST',
      body: { purpose, origin: getSessionBaseUrl() },
    });
  },

  claimCompanionPairCode(code: string) {
    return apiRequest<{
      wsTicket: string;
      tableId: string;
      seatId: string | null;
      sessionType: SessionType;
      purpose: 'private_hand' | 'obs_dock' | 'overhead_camera';
    }>('/api/tables/pair-claim', { method: 'POST', body: { code } });
  },

  rotateOverlaySecret(tableId: string) {
    return apiRequest<{ ok: boolean; overlaySecret: string }>(
      `/api/tables/${encodeURIComponent(tableId)}/rotate-overlay-secret`,
      { method: 'POST' }
    );
  },

  setOverlayDelayedHandReveal(tableId: string, enabled: boolean) {
    return apiRequest<{ ok: boolean; overlayDelayedHandSeatId: string | null }>(
      `/api/tables/${encodeURIComponent(tableId)}/overlay-hand-reveal`,
      { method: 'POST', body: { enabled } }
    );
  },

  testTwitchIntegration(params: {
    tableId: string;
    overlaySecret: string;
    kind: 'card_lookup' | 'deck_lookup' | 'channel_point' | 'poll_result';
    query?: string;
    viewerName?: string;
  }) {
    return apiRequest<{ ok: boolean; reply: string; cardName?: string }>(
      '/api/integrations/twitch/webhook',
      { method: 'POST', body: params }
    );
  },

  optimizeDeckCart(params: {
    deck?: ImportedDeckPayload | null;
    items?: CartDeckItem[] | null;
    includeSideboard?: boolean;
    ownedCollectionText?: string;
    preferences?: Partial<CartOptimizationPreferences>;
  }) {
    return apiRequest<{
      items: CartDeckItem[];
      quotes: VendorQuote[];
      result: OptimizedDeckCartResult;
    }>('/api/cart/optimize', { method: 'POST', body: params });
  },

  reoptimizeDeckCart(params: {
    deck?: ImportedDeckPayload | null;
    items?: CartDeckItem[] | null;
    quotes?: VendorQuote[];
    previousResult: OptimizedDeckCartResult;
    stockUpdates?: StockVerificationUpdate[];
    ownedCollectionText?: string;
    preferences?: Partial<CartOptimizationPreferences>;
  }) {
    return apiRequest<ReoptimizedCartOutcome>('/api/cart/reoptimize', {
      method: 'POST',
      body: params,
    });
  },

  listPriceAlerts() {
    return apiRequest<{ alerts: DeckPriceAlertDTO[] }>('/api/cart/alerts');
  },

  createPriceAlert(params: {
    deckName: string;
    targetTotalUsd: number;
    deck?: ImportedDeckPayload | null;
    items?: CartDeckItem[] | null;
    ownedCollectionText?: string;
    preferences?: Partial<CartOptimizationPreferences>;
  }) {
    return apiRequest<{ alert: DeckPriceAlertDTO }>('/api/cart/alerts', {
      method: 'POST',
      body: params,
    });
  },

  deletePriceAlert(alertId: string) {
    return apiRequest<{ ok: boolean }>(`/api/cart/alerts/${encodeURIComponent(alertId)}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Applies fast client-side optimistic update for instant tactile drag/tap feel,
 * reconciled automatically as soon as the authoritative `GAME_VIEW` frame arrives.
 */
export function applyOptimisticIntentToView(
  view: ClientGameView,
  intent: GameIntent
): ClientGameView {
  const sid = view.viewerSeatId;
  if (!sid || !view.seats[sid]) return view;
  const next = structuredClone(view);
  const mySeat = next.seats[sid];

  if (intent.type === 'TAP_CARD') {
    for (const seat of Object.values(next.seats)) {
      const target = seat.zones.battlefield.find((c) => c.instanceId === intent.instanceId);
      if (target) {
        target.tapped = intent.tapped ?? !target.tapped;
      }
    }
  } else if (intent.type === 'UNTAP_ALL') {
    for (const c of mySeat.zones.battlefield) {
      c.tapped = false;
    }
  } else if (intent.type === 'ADJUST_LIFE') {
    const tSid = intent.targetSeatId ?? sid;
    if (next.seats[tSid]) {
      next.seats[tSid].life += intent.delta;
    }
  } else if (intent.type === 'ADJUST_MANA_POOL') {
    mySeat.manaPool[intent.color] = Math.max(0, (mySeat.manaPool[intent.color] ?? 0) + intent.delta);
  } else if (intent.type === 'CLEAR_MANA_POOL') {
    mySeat.manaPool = { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 };
  } else if (intent.type === 'MOVE_CARD' && mySeat.zones.handCards) {
    const idx = mySeat.zones.handCards.findIndex((c) => c.instanceId === intent.instanceId);
    if (idx !== -1 && intent.toZone === 'battlefield') {
      const [moved] = mySeat.zones.handCards.splice(idx, 1);
      mySeat.zones.handCount = Math.max(0, mySeat.zones.handCount - 1);
      moved.zone = 'battlefield';
      moved.tapped = Boolean(intent.tapped);
      moved.faceDown = Boolean(intent.faceDown);
      if (intent.row) moved.row = intent.row;
      mySeat.zones.battlefield.push(moved);
    }
  }

  return next;
}
